"use client";

import { c as _c } from "react/compiler-runtime";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { BrainIcon, ChevronDownIcon } from "lucide-react";
import { createContext, memo, use, useEffect, useRef, useState } from "react";
import { Streamdown } from "streamdown";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import type { TranslationValues } from "@llmgateway/i18n";
import type { ComponentProps, ReactNode } from "react";
import type { BundledTheme } from "shiki";
const shikiTheme: [BundledTheme, BundledTheme] = ["github-light", "github-dark"];
interface ReasoningContextValue {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
}
const ReasoningContext = createContext<ReasoningContextValue | null>(null);
export const useReasoning = () => {
  const context = use(ReasoningContext);
  if (!context) {
    throw new Error("Reasoning components must be used within Reasoning");
  }
  return context;
};
export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};
const AUTO_CLOSE_DELAY = 1000;
const MS_IN_S = 1000;
export const Reasoning = memo(t0 => {
  const $ = _c(44);
  let children;
  let className;
  let durationProp;
  let onOpenChange;
  let open;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      isStreaming: t1,
      open,
      defaultOpen: t2,
      onOpenChange,
      duration: durationProp,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = durationProp;
    $[4] = onOpenChange;
    $[5] = open;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    durationProp = $[3];
    onOpenChange = $[4];
    open = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const isStreaming = t1 === undefined ? false : t1;
  const defaultOpen = t2 === undefined ? true : t2;
  let t3;
  if ($[9] !== defaultOpen || $[10] !== onOpenChange || $[11] !== open) {
    t3 = {
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange
    };
    $[9] = defaultOpen;
    $[10] = onOpenChange;
    $[11] = open;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const [isOpen, setIsOpen] = useControllableState(t3);
  let t4;
  if ($[13] !== durationProp) {
    t4 = {
      prop: durationProp,
      defaultProp: undefined
    };
    $[13] = durationProp;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const [duration, setDuration] = useControllableState(t4);
  const [hasAutoClosed, setHasAutoClosed] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const wasInitiallyOpen = useRef(defaultOpen);
  let t5;
  let t6;
  if ($[15] !== isStreaming || $[16] !== setDuration || $[17] !== startTime) {
    t5 = () => {
      if (isStreaming) {
        if (startTime === null) {
          setStartTime(Date.now());
        }
      } else {
        if (startTime !== null) {
          setDuration(Math.ceil((Date.now() - startTime) / MS_IN_S));
          setStartTime(null);
        }
      }
    };
    t6 = [isStreaming, startTime, setDuration];
    $[15] = isStreaming;
    $[16] = setDuration;
    $[17] = startTime;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[20] !== hasAutoClosed || $[21] !== isOpen || $[22] !== isStreaming || $[23] !== setIsOpen) {
    t7 = () => {
      if (wasInitiallyOpen.current && !isStreaming && isOpen && !hasAutoClosed) {
        const timer = setTimeout(() => {
          setIsOpen(false);
          setHasAutoClosed(true);
        }, AUTO_CLOSE_DELAY);
        return () => clearTimeout(timer);
      }
    };
    t8 = [isStreaming, isOpen, setIsOpen, hasAutoClosed];
    $[20] = hasAutoClosed;
    $[21] = isOpen;
    $[22] = isStreaming;
    $[23] = setIsOpen;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  useEffect(t7, t8);
  let t9;
  if ($[26] !== setIsOpen) {
    t9 = newOpen => {
      setIsOpen(newOpen);
    };
    $[26] = setIsOpen;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const handleOpenChange = t9;
  let t10;
  if ($[28] !== duration || $[29] !== isOpen || $[30] !== isStreaming || $[31] !== setIsOpen) {
    t10 = {
      isStreaming,
      isOpen,
      setIsOpen,
      duration
    };
    $[28] = duration;
    $[29] = isOpen;
    $[30] = isStreaming;
    $[31] = setIsOpen;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== className) {
    t11 = cn("not-prose mb-4", className);
    $[33] = className;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== children || $[36] !== handleOpenChange || $[37] !== isOpen || $[38] !== props || $[39] !== t11) {
    t12 = <Collapsible className={t11} onOpenChange={handleOpenChange} open={isOpen} {...props}>{children}</Collapsible>;
    $[35] = children;
    $[36] = handleOpenChange;
    $[37] = isOpen;
    $[38] = props;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t10 || $[42] !== t12) {
    t13 = <ReasoningContext value={t10}>{t12}</ReasoningContext>;
    $[41] = t10;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
});
export type ReasoningTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => ReactNode;
};
type Translate = (key: string, values?: TranslationValues) => string;
const getDefaultThinkingMessage = (t: Translate, isStreaming: boolean, duration?: number) => {
  if (isStreaming || duration === 0) {
    return <Shimmer duration={1}>{t("aiElements.thinking")}</Shimmer>;
  }
  if (duration === undefined) {
    return <p>{t("aiElements.thoughtFewSeconds")}</p>;
  }
  return <p>{t("aiElements.thoughtSeconds", {
      duration
    })}</p>;
};
export const ReasoningTrigger = memo(t0 => {
  const $ = _c(18);
  let children;
  let className;
  let getThinkingMessage;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      getThinkingMessage,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = getThinkingMessage;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    getThinkingMessage = $[3];
    props = $[4];
  }
  const {
    isStreaming,
    isOpen,
    duration
  } = useReasoning();
  const {
    t
  } = useI18n();
  let t1;
  if ($[5] !== className) {
    t1 = cn("flex w-full items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== duration || $[9] !== getThinkingMessage || $[10] !== isOpen || $[11] !== isStreaming || $[12] !== t) {
    t2 = children ?? <><BrainIcon className="size-4" />{getThinkingMessage ? getThinkingMessage(isStreaming, duration) : getDefaultThinkingMessage(t, isStreaming, duration)}<ChevronDownIcon className={cn("size-4 transition-transform", isOpen ? "rotate-180" : "rotate-0")} /></>;
    $[7] = children;
    $[8] = duration;
    $[9] = getThinkingMessage;
    $[10] = isOpen;
    $[11] = isStreaming;
    $[12] = t;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== props || $[15] !== t1 || $[16] !== t2) {
    t3 = <CollapsibleTrigger className={t1} {...props}>{t2}</CollapsibleTrigger>;
    $[14] = props;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
});
export type ReasoningContentProps = ComponentProps<typeof CollapsibleContent> & {
  children: string;
};
export const ReasoningContent = memo(t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("mt-4 text-sm", "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-muted-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props) {
    t2 = <Streamdown shikiTheme={shikiTheme} {...props}>{children}</Streamdown>;
    $[6] = children;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== t1 || $[11] !== t2) {
    t3 = <CollapsibleContent className={t1} {...props}>{t2}</CollapsibleContent>;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
});
Reasoning.displayName = "Reasoning";
ReasoningTrigger.displayName = "ReasoningTrigger";
ReasoningContent.displayName = "ReasoningContent";